import classnames from "classnames";
import { isEmpty } from "lodash";
import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { AccordionItem } from "@krea/customer-features/shared-components/accordion/AccordionItem";
import EditablePreviewFieldRequest from "@krea/customer-features/shared-components/field-request/EditablePreviewFieldRequest";
import Preloader from "@krea/customer-features/shared-components/preloader";
import { Text } from "@krea/customer-features/shared-components/text";
import { Toast } from "@krea/customer-features/shared-components/toast/Toast";
import { useGetFieldRequests } from "@krea/customer-features/store/field-requests/hooks/queries";
import { IFieldRequest } from "@krea/customer-features/types/fieldRequest";
import {
  getFilteredFieldRequests,
  PRODUCT_TYPE,
} from "@krea/customer-features/utils";

import { useMobile } from "../../hooks";
import { useGetCachedCurrentLoanApplication } from "../../store/current-loan-application/hooks";

type ICategories = Record<string, string[]>;

const fieldRequestsCategories: ICategories = {
  basicInformation: [
    "requestedAmount",
    "repaymentTime",
    "reasonExplanation",
    "reason",
    "leasing",
    "unpaidInvoices",
    "property",
  ],
  collateralInformation: [
    "propertyAdditionalData",
    "leasingAdditionalData",
    "guarantors",
    "companyMortgage",
    "unpaidInvoicesAdditionalData",
    "parentCompanyGuarantee",
    "shareMortgage",
  ],
  repayment: ["refinancing", "propertyCashFlow"],
  financial: [
    "selfEstimatedHistoricRevenue",
    "selfEstimatedRevenue",
    "accountingData",
    "bankingData",
    "refinancingAmounts",
    "additionalSecurities",
  ],
  other: ["constructionEstimate", "propertyOwner", "businessPlan", "referrer"],
};

const fieldRequestsCategoriesWithAdditionalFinancingOffers: ICategories = {
  basicInformation: [
    "requestedAmount",
    "repaymentTime",
    "reasonExplanation",
    "reason",
  ],
  collateralInformation: ["guarantors"],
  financial: [
    "selfEstimatedHistoricRevenue",
    "selfEstimatedRevenue",
    "accountingData",
    "bankingData",
    "refinancingAmounts",
    "additionalSecurities",
  ],
  factoringOffers: ["unpaidInvoices", "unpaidInvoicesAdditionalData"],
  leasingOffers: ["leasing", "leasingAdditionalData"],
  realEstateOffers: ["property", "propertyAdditionalData"],
};

type ICategorizedFieldRequests = Record<string, PreviewFieldRequest[]>;

interface PreviewFieldRequest {
  field: string;
  id: string;
}

// Get the list of field requests that are allowed to be shown in the preview, grouped by category
const getCategories = (
  previewFieldRequests: IFieldRequest[],
  showAdditionalFinancingOffers: boolean,
) => {
  const categories: ICategorizedFieldRequests = {};

  if (showAdditionalFinancingOffers) {
    Object.entries(
      fieldRequestsCategoriesWithAdditionalFinancingOffers,
    ).forEach(([category, fields]) => {
      categories[category] = previewFieldRequests
        .filter((request: { field: string }) => fields.includes(request.field))
        .map((it) => ({ id: it.id, field: it.field }));
    });
  } else {
    Object.entries(fieldRequestsCategories).forEach(([category, fields]) => {
      categories[category] = previewFieldRequests
        .filter((request: { field: string }) => fields.includes(request.field))
        .map((it) => ({ id: it.id, field: it.field }));
    });
  }

  return categories;
};

const AppQuestionsPreviewSection = ({
  title,
  description,
  className,
  children,
}: {
  title: string;
  description?: string;
  className?: string;
  children: React.ReactNode;
}) => {
  return (
    <section className={classnames("tw-py-6", className)}>
      <div className="tw-mb-6">
        <h2 className="tw-m-0 tw-mb-2 tw-text-2xl tw-font-semibold">{title}</h2>

        {description && (
          <div
            className={classnames(
              "tw-mt-2 tw-text-sm tw-font-semibold tw-leading-6",
            )}
          >
            {description}
          </div>
        )}
      </div>

      <div className={"tw-grid tw-min-w-0 tw-grid-cols-1 tw-gap-6"}>
        {children}
      </div>
    </section>
  );
};

const AdditionalFinancingOffersQuestions = ({
  title,
  fieldRequests,
  disableEdit,
}: {
  title: string;
  fieldRequests: string[];
  disableEdit?: boolean;
}) => {
  return (
    !isEmpty(fieldRequests) && (
      <AccordionItem
        title={title}
        titleStyling="tw-font-bold tw-pb-0 tw-m-0 tw-pr-4"
      >
        {fieldRequests.map((fieldRequestID, index: number) => (
          <div className={index === 0 ? "tw-pb-6" : ""} key={fieldRequestID}>
            <EditablePreviewFieldRequest
              key={fieldRequestID}
              fieldRequestID={fieldRequestID}
              preview
              disableEdit={disableEdit}
            />
          </div>
        ))}
      </AccordionItem>
    )
  );
};

const AppQuestionsPreview = ({
  className,
  disableEdit,
  previewFields,
}: {
  className?: string;
  disableEdit?: boolean;
  previewFields?: string[];
}) => {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const { data: fieldRequests = [], isLoading } = useGetFieldRequests();
  const previewFieldRequests = getFilteredFieldRequests(
    previewFields,
    fieldRequests,
  );

  const { data: { product } = {} } = useGetCachedCurrentLoanApplication();
  const showAdditionalFinancingOffers =
    (product as string) === PRODUCT_TYPE.COMPANY_LOAN.toString();
  const categorizedFieldRequests = getCategories(
    previewFieldRequests,
    showAdditionalFinancingOffers,
  );

  return (
    <>
      <div
        className={classnames(
          "tw-p-6",
          {
            "tw-mb-12": !showAdditionalFinancingOffers && isMobile,
            "tw-mb-6": showAdditionalFinancingOffers,
          },
          className,
        )}
        style={{ border: "5px solid #fbf2ff", borderRadius: "8px" }}
      >
        {isLoading ? (
          <Preloader className="tw-py-6 tw-text-primary" />
        ) : (
          <Fragment>
            {disableEdit ? (
              <Text className="tw-border-b tw-pb-6 tw-font-bold">
                {t("iaq.applicationPreview.bidSelected.subheading")}
              </Text>
            ) : (
              <Toast
                innerText={{
                  subtitle: t("iaq.applicationPreview.explainerParagraph"),
                }}
              />
            )}

            <div>
              {!isEmpty(categorizedFieldRequests.basicInformation) && (
                <AppQuestionsPreviewSection
                  title={t("iaq.applicationPreview.basicInformationHeading")}
                  className="tw-border-b last:tw-border-b-0"
                >
                  {categorizedFieldRequests.basicInformation.map((it) => (
                    <EditablePreviewFieldRequest
                      key={it.id}
                      fieldRequestID={it.id}
                      preview
                      disableEdit={disableEdit}
                    />
                  ))}
                </AppQuestionsPreviewSection>
              )}

              {!isEmpty(categorizedFieldRequests.collateralInformation) && (
                <AppQuestionsPreviewSection
                  title={t(
                    "iaq.applicationPreview.collateralInformationHeading",
                  )}
                  className="tw-border-b last:tw-border-b-0"
                >
                  {categorizedFieldRequests.collateralInformation.map((it) => (
                    <EditablePreviewFieldRequest
                      key={it.id}
                      fieldRequestID={it.id}
                      preview
                      disableEdit={disableEdit}
                    />
                  ))}
                </AppQuestionsPreviewSection>
              )}

              {!isEmpty(categorizedFieldRequests.repayment) && (
                <AppQuestionsPreviewSection
                  title={t("iaq.applicationPreview.repaymentHeading")}
                  className="tw-border-b last:tw-border-b-0"
                >
                  {categorizedFieldRequests.repayment.map((it) => (
                    <EditablePreviewFieldRequest
                      key={it.id}
                      fieldRequestID={it.id}
                      preview
                      disableEdit={disableEdit}
                    />
                  ))}
                </AppQuestionsPreviewSection>
              )}

              {!isEmpty(categorizedFieldRequests.financial) && (
                <AppQuestionsPreviewSection
                  title={t(
                    "iaq.applicationPreview.financialInformationHeading",
                  )}
                  className="tw-border-b last:tw-border-b-0"
                >
                  {categorizedFieldRequests.financial.map((it) => (
                    <EditablePreviewFieldRequest
                      key={it.id}
                      fieldRequestID={it.id}
                      preview
                      disableEdit={disableEdit}
                    />
                  ))}
                </AppQuestionsPreviewSection>
              )}

              {!isEmpty(categorizedFieldRequests.other) && (
                <AppQuestionsPreviewSection
                  title={t("iaq.applicationPreview.otherInformationHeading")}
                  className="tw-border-b last:tw-border-b-0"
                >
                  {categorizedFieldRequests.other.map((it) => (
                    <EditablePreviewFieldRequest
                      key={it.id}
                      fieldRequestID={it.id}
                      preview
                      disableEdit={disableEdit}
                    />
                  ))}
                </AppQuestionsPreviewSection>
              )}
            </div>
          </Fragment>
        )}
      </div>

      {showAdditionalFinancingOffers && (
        <div
          className={classnames("tw-p-6", { "tw-mb-12": isMobile }, className)}
          style={{ border: "5px solid #e9ecef", borderRadius: "8px" }}
        >
          <Toast
            innerText={{
              subtitle: t(
                "iaq.applicationPreview.additionalFinancingOffersExplainer",
              ),
            }}
          />

          <AppQuestionsPreviewSection
            title={t("iaq.applicationPreview.additionalFinancingOffers")}
          >
            <AdditionalFinancingOffersQuestions
              title={t("iaq.applicationPreview.qualifyForFactoringOffers")}
              fieldRequests={categorizedFieldRequests.factoringOffers.map(
                (it) => it.id,
              )}
              disableEdit={disableEdit}
            />
            <AdditionalFinancingOffersQuestions
              title={t("iaq.applicationPreview.qualifyForLeasingOffers")}
              fieldRequests={categorizedFieldRequests.leasingOffers.map(
                (it) => it.id,
              )}
              disableEdit={disableEdit}
            />
            <AdditionalFinancingOffersQuestions
              title={t("iaq.applicationPreview.qualifyForRealEstateOffers")}
              fieldRequests={categorizedFieldRequests.realEstateOffers.map(
                (it) => it.id,
              )}
              disableEdit={disableEdit}
            />
          </AppQuestionsPreviewSection>
        </div>
      )}
    </>
  );
};

export default AppQuestionsPreview;
